import type { AxiosRequestConfig, AxiosResponse } from 'axios';

import axiosInstance from '@api/axios.ts';
import { urls } from '@api/urls.ts';

/**
 * Admin settings, super admins only (ZLAYT-1682). Choices such as what an
 * empty demand letter section shows are settings an admin changes here
 * instead of a code change. Every change is logged with who, when, the old
 * and the new value.
 */

export type AdminSettingType = 'choice' | 'number' | 'string_list';
export type AdminSettingValue = string | number | string[];

export interface AdminSetting {
  key: string;
  type: AdminSettingType;
  label: string;
  description: string;
  group: string;
  /** How to write one line of a list setting. */
  item_help: string;
  choices: { value: string; label: string }[];
  min_value: number | null;
  max_value: number | null;
  max_items: number | null;
  default: AdminSettingValue;
  value: AdminSettingValue;
  /** True while no admin has saved a value (the default applies). */
  is_default: boolean;
  /** True while an older server setting, not the default, sets the value. */
  server_override: boolean;
  updated_by: string | null;
  updated_at: string | null;
}

export interface AdminSettingChange {
  id: number;
  key: string;
  label: string;
  action: 'set' | 'reset';
  /** null: no saved value, the default applied. */
  old_value: AdminSettingValue | null;
  new_value: AdminSettingValue | null;
  changed_by: string | null;
  changed_at: string;
}

export interface AdminSettingChanges {
  count: number;
  next: string | null;
  previous: string | null;
  results: AdminSettingChange[];
}

/**
 * The shared axios instance rejects with the response body alone, so the
 * answers the page acts on come back as responses and are rejected here
 * with their status kept (a 400 carries the reason a value was refused).
 */
const HANDLED = [400, 403, 404];

export class AdminSettingsRequestError extends Error {
  response: { status: number; data: unknown };

  constructor(status: number, data: unknown) {
    super(`Request failed with status ${status}`);
    this.name = 'AdminSettingsRequestError';
    this.response = { status, data };
  }
}

const config: AxiosRequestConfig = {
  validateStatus: (status) =>
    (status >= 200 && status < 300) || HANDLED.includes(status),
};

const dataOf = <T>(response: AxiosResponse<T>): T => {
  if (HANDLED.includes(response.status)) {
    throw new AdminSettingsRequestError(response.status, response.data);
  }
  return response.data;
};

export const listAdminSettings = async (): Promise<AdminSetting[]> =>
  dataOf(await axiosInstance.get(urls.adminSettings.list, config));

export const saveAdminSetting = async (
  key: string,
  value: AdminSettingValue
): Promise<AdminSetting> =>
  dataOf(
    await axiosInstance.patch(urls.adminSettings.detail(key), { value }, config)
  );

export const resetAdminSetting = async (key: string): Promise<AdminSetting> =>
  dataOf(await axiosInstance.post(urls.adminSettings.reset(key), {}, config));

export const listAdminSettingChanges = async (
  page = 1
): Promise<AdminSettingChanges> =>
  dataOf(
    await axiosInstance.get(urls.adminSettings.changes, {
      ...config,
      params: { page },
    })
  );
